import { DEFAULT_PALETTE } from '../../core/palette';
import { SHATTER_PATTERN_OPTIONS } from '../../core/shatter';
import type { ParamSchema, SchemaValues } from '../../core/params/types';

export const FRAGMENTATION_PARAMS = [
  {
    kind: 'int',
    key: 'fragments',
    label: 'Fragmente',
    min: 4,
    max: 3000,
    step: 1,
    scale: 'log',
    default: 120,
    bulkRange: [20, 600],
    group: 'Bruch',
  },
  {
    kind: 'enum',
    key: 'pattern',
    label: 'Bruchmuster',
    default: 'voronoi',
    group: 'Bruch',
    options: SHATTER_PATTERN_OPTIONS,
  },
  {
    kind: 'float',
    key: 'gap',
    label: 'Lückenbreite',
    min: 0,
    max: 20,
    step: 0.1,
    default: 1.2,
    unit: 'px',
    group: 'Bruch',
    tooltip: 'Schrumpft jedes Fragment, sodass die Bruchlinien sichtbar werden.',
  },

  {
    kind: 'float',
    key: 'displacement',
    label: 'Verschiebung',
    min: 0,
    max: 400,
    step: 1,
    default: 26,
    unit: 'px',
    bulkRange: [0, 140],
    group: 'Verschiebung',
  },
  {
    kind: 'enum',
    key: 'displacementMode',
    label: 'Richtung',
    default: 'radial',
    segmented: true,
    group: 'Verschiebung',
    options: [
      { value: 'radial', label: 'Radial' },
      { value: 'random', label: 'Zufällig' },
      { value: 'gravity', label: 'Fallend' },
    ],
  },
  {
    kind: 'float',
    key: 'distanceFalloff',
    label: 'Abstandskopplung',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.7,
    group: 'Verschiebung',
    tooltip: 'Fragmente weiter vom Zentrum wandern stärker.',
    showIf: (v) => v.displacementMode === 'radial',
  },
  {
    kind: 'float',
    key: 'rotation',
    label: 'Rotation',
    min: 0,
    max: 180,
    step: 1,
    default: 12,
    unit: '°',
    group: 'Verschiebung',
  },
  {
    kind: 'float',
    key: 'scaleJitter',
    label: 'Skalierungsstreuung',
    min: 0,
    max: 1,
    step: 0.01,
    default: 0.1,
    group: 'Verschiebung',
  },

  {
    kind: 'enum',
    key: 'style',
    label: 'Stil',
    default: 'fill',
    segmented: true,
    group: 'Darstellung',
    options: [
      { value: 'fill', label: 'Füllung' },
      { value: 'stroke', label: 'Kontur' },
      { value: 'both', label: 'Beides' },
    ],
  },
  {
    kind: 'float',
    key: 'lineWidth',
    label: 'Strichstärke',
    min: 0.2,
    max: 8,
    step: 0.1,
    default: 1,
    unit: 'px',
    group: 'Darstellung',
    showIf: (v) => v.style !== 'fill',
  },
  {
    kind: 'float',
    key: 'opacity',
    label: 'Deckkraft',
    min: 0.05,
    max: 1,
    step: 0.01,
    default: 1,
    group: 'Darstellung',
  },

  {
    kind: 'enum',
    key: 'colorMode',
    label: 'Farbmodus',
    default: 'distance',
    group: 'Farbe',
    options: [
      { value: 'solid', label: 'Einfarbig' },
      { value: 'distance', label: 'Nach Zentrumsdistanz' },
      { value: 'size', label: 'Nach Fragmentgrösse' },
      { value: 'random', label: 'Zufällig' },
    ],
  },
  {
    kind: 'palette',
    key: 'palette',
    label: 'Palette',
    default: { ...DEFAULT_PALETTE, paletteId: 'riso' },
    group: 'Farbe',
  },
] as const satisfies ParamSchema;

export type FragmentationValues = SchemaValues<typeof FRAGMENTATION_PARAMS>;
